import React, { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GeometryType } from "@evergis/api";
import { ButtonsGroup, IButtonItemProps, IconTypesKeys, Tree, TreeId, useDialogShadow } from "@evergis/ui";

import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../DialogComponents/FullScreenDialog";
import { ImportWizard } from "./ImportWizard";
import { AttributesBlockContainer, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { ImportDialogContent, ImportTitleButtons } from "../styled";
import { useImportContext } from "../importContext";
import { useContext } from "../hooks/useContext";
import { concatObjects } from "../utils/concatObjects";
import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";
import { ImportObject } from "../types";

// eslint-disable-next-line max-lines-per-function
export const ImportObjects: FC = () => {
  const { t } = useTranslation("common");
  const context = useContext();
  const { shadows, setScrollContainer } = useDialogShadow();
  const { prevStep, nextStep, file, dataSchema, setObjects, geometryType, setGeometryType } = useImportContext();

  const getObjectIcon = useCallback((type?: GeometryType): IconTypesKeys => {
    switch (type) {
      case GeometryType.Point:
        return "geom_point";
      case GeometryType.Polyline:
        return "geom_polyline";
      case GeometryType.Polygon:
        return "geom_polygon";
      default:
        return null;
    }
  }, []);

  const convertSchemaToTree = useCallback(
    (children: SchemaParsed[] | SchemaParsed["children"], id = ""): ImportObject[] => {
      return children?.length
        ? children.map((child, index) => {
            const newId = id ? [id, index + 1].join("-") : (index + 1).toString();

            return {
              id: newId,
              text: child.name || child.sourceName,
              type: child.layerDefinition?.geometryType || null,
              icon: getObjectIcon(child.layerDefinition?.geometryType),
              chip: child.objectCount > -1 ? child.objectCount : null,
              children: convertSchemaToTree(child.children, newId),
            };
          })
        : [];
    },
    [getObjectIcon],
  );

  const objects = useMemo(() => convertSchemaToTree(dataSchema as SchemaParsed[]), [convertSchemaToTree, dataSchema]);

  const typeOptions: IButtonItemProps[] = useMemo(
    () => [
      { label: t("points"), kind: "geom_point", iconWithLabel: true, value: GeometryType.Point },
      { label: t("lines"), kind: "geom_polyline", iconWithLabel: true, value: GeometryType.Polyline },
      { label: t("polygons"), kind: "geom_polygon", iconWithLabel: true, value: GeometryType.Polygon },
    ],
    [t],
  );

  const hasObjectsWithType = useCallback(
    (items: ImportObject[], type: GeometryType): boolean =>
      items.some(item => item.type === type || (item.children?.length && hasObjectsWithType(item.children, type))),
    [],
  );

  const getDefaultType: IButtonItemProps = useMemo(
    () =>
      typeOptions.find(type =>
        geometryType ? geometryType === type.value : hasObjectsWithType(objects, type.value as GeometryType),
      ),
    [geometryType, hasObjectsWithType, objects, typeOptions],
  );

  const [selectedType, setSelectedType] = useState<IButtonItemProps>(getDefaultType);
  const [expanded, setExpanded] = useState<TreeId[]>();
  const [selected, setSelected] = useState<TreeId[]>();

  const filteredTypes: IButtonItemProps[] = useMemo(
    () => typeOptions.filter(type => hasObjectsWithType(objects, type.value as GeometryType)),
    [hasObjectsWithType, objects, typeOptions],
  );

  const disableObjects = useCallback(
    (items: ImportObject[]): ImportObject[] =>
      items.map(item => ({
        ...item,
        disabled: item.type !== null && item.type !== selectedType.value,
        children: item.children?.length ? disableObjects(item.children) : [],
      })),
    [selectedType.value],
  );

  const formattedObjects: ImportObject[] = useMemo(
    () => [
      {
        id: 0,
        text: file.name,
        children: disableObjects(objects),
      },
    ],
    [file.name, disableObjects, objects],
  );

  const onApply = useCallback(() => {
    setObjects(concatObjects(objects, selected));
    setGeometryType(selectedType.value as GeometryType);
    nextStep();
  }, [nextStep, objects, selected, selectedType.value, setGeometryType, setObjects]);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle
        displayShadow={shadows.upper}
        extra={
          <>
            <ImportWizard />
            <ImportTitleButtons>
              <ButtonsGroup selected={selectedType} options={filteredTypes} onClick={setSelectedType} />
            </ImportTitleButtons>
          </>
        }
      />
      <ImportDialogContent ref={setScrollContainer}>
        <AttributesBlockContainer>
          <RestrictedWidthContainer style={{ paddingTop: "1rem" }}>
            <Tree
              data={formattedObjects}
              selectable
              selected={selected}
              expanded={expanded}
              allExpanded
              allSelected
              setExpanded={setExpanded}
              setSelected={setSelected}
            />
          </RestrictedWidthContainer>
        </AttributesBlockContainer>
      </ImportDialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyText={t("actions.next")}
        backButtonText={t("import.back", { ns: "dialogs" })}
        applyDisabled={!selected?.length}
        onBackButton={prevStep}
        onApply={onApply}
      />
    </FullScreenDialog>
  );
};
